
import {
  defineComponent,
  computed,
} from '@vue/composition-api';
import { FCCard } from '@fc/angie-ui';
import FCSwiper from '~/components/FCSwiper.vue';
import FCImage from '~/components/FCImage.vue';
import Section from '~/components/v5/Section.vue';
import SectionHeader from '~/components/v5/SectionHeader.vue';
import { isMobileEffect } from '~/effects/matchMedia';

export default defineComponent({
  name: 'TheTechSection',
  components: {
    FCCard,
    FCSwiper,
    FCImage,
    Section,
    SectionHeader,
  },
  setup() {
    const fcImageDefaults = {
      displayType: 'block',
      fcProvider: 'storyblok',
    } as const;

    const techCards = [
      {
        id: 1,
        heading: 'Track Every Strike',
        description: 'Precision trackers measure every punch, round, and streak — giving you proof of effort and progress you can feel.',
        image: 'https://a.storyblok.com/f/152150/936x1162/d4a0661838/track-image-2x.jpg',
        imageAlt: 'A man in sportswear performs a high kick on a punching bag in a gym, with a digital display showing 91 strikes out of a 95 goal.',
      },
      {
        id: 2,
        heading: 'Form and Technique Feedback',
        description: 'AI algorithm detects every punch and combination in real time, confirming when your strikes are clean — and calling out when they\'re not.',
        image: 'https://a.storyblok.com/f/152150/936x1162/ab7cc9f887/form-image.jpg',
        imageAlt: 'Person in dim room wearing boxing gloves punches a standing heavy bag; game-style overlay shows "PERFECT!" with progress bars and a red checkmark.',
      },
      {
        id: 3,
        heading: 'Power and Speed Feedback',
        description: 'See your output in real time — power, velocity, and volume — the same metrics fighters use to push past their limits.',
        image: 'https://a.storyblok.com/f/152150/936x1162/769e97f58e/group-1533208267.jpg',
        imageAlt: 'A person wearing boxing gloves punches a Fight Camp bag indoors, with a digital display showing "19 Total Output" and additional numbers.',
      },
      {
        id: 4,
        heading: 'VS Mode',
        description: 'Train together on one FightCamp system — both wearing trackers, both doing the same workout. Compare output in real time or unlock AI opponents up to level 10.',
        image: 'https://a.storyblok.com/f/152150/936x1162/8216ee6470/vs-mode-image.jpg',
        imageAlt: 'Two men boxing a standing punching bag in a garage, with on-screen overlays showing punch counts and scores.',
      },
      {
        id: 5,
        heading: 'Calories and HR Zones',
        description: 'Track your calories and heart rate zones to hold yourself accountable — pushing hard enough to stay in your target zone, sustain performance, and stay aligned with your fitness goals.',
        image: 'https://a.storyblok.com/f/152150/936x1162/fc4ed52d56/bpm-image.jpg',
        imageAlt: 'Woman boxing in gym with overlaid heart-rate graph showing peak 163 BPM and 195 calories burned.',
      },
      {
        id: 6,
        heading: 'Streaks and Challenges',
        description: 'Stay accountable with streaks, milestones, and leaderboards that reward consistency — the mark of a true fighter.',
        image: 'https://a.storyblok.com/f/152150/936x1162/35acfd4225/streaks-image-2x.jpg',
        imageAlt: 'Person in a home gym wrapping hands as TV shows a boxing workout; overlay reads "August Solo Challenge — 630 points needed to earn Silver."',
      },
      {
        id: 7,
        heading: 'Progression History',
        description: 'Review your training journey over time — every round, every improvement, every win. See the grind become growth.',
        image: 'https://a.storyblok.com/f/152150/936x1162/d881340ea8/progression-2x.jpg',
        imageAlt: 'Woman exercising with resistance band in a gym; translucent line chart overlay showing rising red and gray lines.',
      },
    ];

    const systemCards = computed(() => (techCards));
    const isMobile = isMobileEffect();

    return {
      systemCards,
      isMobile,
      fcImageDefaults,
    };
  },
});
